import { IonButton, IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  INVITATION_REFUSAL,
  type InvitationAcceptRequest,
  type InvitationDetail,
  type InvitationRefusal,
  acceptInvitation,
  fetchInvitation,
  invitationRefusalOf,
} from '~/api/invitations';
import { queryKeys } from '~/api/queryKeys';
import type { Tenant } from '~/api/tenant';
import { Alert } from '~/components/Alert';
import { OfficeLogo } from '~/components/OfficeLogo';
import { SuspendedNotice } from '~/components/SuspendedNotice';
import { useTenant } from '~/components/TenantGate';
import { FieldWrap, MaskedInput } from '~/components/forms/FormField';
import { useAppNavigation } from '~/hooks/useAppNavigation';
import { formatDateTime } from '~/lib/format';
import { applyCpfMask, applyPhoneMask } from '~/lib/masks';
import { ROUTES, convitePath } from '~/routes';
import {
  EMPTY_ACCEPT_PAYLOAD,
  EMPTY_INVITATION_FORM,
  type InvitationField,
  type InvitationFieldErrors,
  type InvitationForm,
  checkInvitationAccept,
  invitationFieldErrors,
} from '~/schemas/invitation';
import {
  ACCESS_LEVEL_LABEL,
  type AccessLevel,
  COMPANY_ROLE_LABEL,
  FISCAL_MODULES,
  MODULE_LABEL,
} from '~/session/access';
import { clearLocalData } from '~/session/clearLocalData';
import { INVITATION_ACCEPTED_NOTICE } from '~/session/loginHandoff';
import { useAuthStore } from '~/stores/auth';

const OPEN_INVITATION_FAILURE = 'Não foi possível abrir o convite';
const ACCEPT_INVITATION_FAILURE = 'Não foi possível aceitar o convite. Tente de novo.';

const LEVEL_TAG_CLASS: Record<AccessLevel, string> = {
  NONE: 'rx-tag',
  READ: 'rx-tag rx-tag-warn',
  FULL: 'rx-tag rx-tag-pos',
};

/** Como a tela mostra cada recusa; o que não tem código cai no aviso com "Tentar novamente". */
const REFUSAL_LAYOUT = {
  suspended: 'suspended',
  login: 'login',
  message: 'message',
  retry: 'retry',
} as const;

type RefusalLayout = (typeof REFUSAL_LAYOUT)[keyof typeof REFUSAL_LAYOUT];

const REFUSAL_LAYOUT_BY_CODE: Record<string, RefusalLayout> = {
  [INVITATION_REFUSAL.officeSuspended]: REFUSAL_LAYOUT.suspended,
  [INVITATION_REFUSAL.accepted]: REFUSAL_LAYOUT.login,
  [INVITATION_REFUSAL.emailTaken]: REFUSAL_LAYOUT.login,
  [INVITATION_REFUSAL.invalid]: REFUSAL_LAYOUT.message,
  [INVITATION_REFUSAL.expired]: REFUSAL_LAYOUT.message,
};

function layoutOf(refusal: InvitationRefusal | null): RefusalLayout {
  if (!refusal) return REFUSAL_LAYOUT.retry;
  return REFUSAL_LAYOUT_BY_CODE[refusal.code] ?? REFUSAL_LAYOUT.retry;
}

function invitationSentence(invitation: InvitationDetail): string {
  const role = COMPANY_ROLE_LABEL[invitation.role];
  return `${invitation.invited_by.full_name} convidou você para ${invitation.client.razao_social} como ${role}`;
}

export function Convite(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const tenant = useTenant();
  const nav = useAppNavigation();
  const queryClient = useQueryClient();
  const openSessionEmail = useAuthStore((state) => state.user?.email ?? '');

  const [form, setForm] = useState<InvitationForm>(EMPTY_INVITATION_FORM);
  const [fieldErrors, setFieldErrors] = useState<InvitationFieldErrors>({});

  const invitationQuery = useQuery({
    queryKey: queryKeys.invitations.byToken(token),
    queryFn: () => fetchInvitation(token),
    retry: false,
    throwOnError: false,
  });

  const acceptMutation = useMutation({
    mutationFn: (payload: InvitationAcceptRequest) => acceptInvitation(token, payload),
    onSuccess: async (accepted) => {
      // A sessão aberta é de outra conta: nada dela pode sobreviver ao aceite. A ordem importa —
      // o login com sessão viva redireciona para o painel.
      if (openSessionEmail) {
        await clearLocalData(queryClient);
      }
      nav.replace(ROUTES.login, { email: accepted.email, notice: INVITATION_ACCEPTED_NOTICE });
    },
    onError: (error) => {
      const refusal = invitationRefusalOf(error);
      setFieldErrors(invitationFieldErrors(refusal?.fieldErrors ?? {}));
    },
  });

  const updateField = (field: InvitationField, value: string): void => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const accept = (invitation: InvitationDetail): void => {
    if (invitation.existing_account) {
      acceptMutation.mutate(EMPTY_ACCEPT_PAYLOAD);
      return;
    }
    const { payload, errors } = checkInvitationAccept(form);
    setFieldErrors(errors);
    if (!payload) return;
    acceptMutation.mutate(payload);
  };

  if (invitationQuery.isPending) {
    return (
      <InvitationScreen tenant={tenant}>
        <Loading />
      </InvitationScreen>
    );
  }

  if (!invitationQuery.data) {
    return (
      <InvitationScreen tenant={tenant}>
        <Refusal
          refusal={invitationRefusalOf(invitationQuery.error)}
          tenant={tenant}
          onRetry={() => invitationQuery.refetch()}
        />
      </InvitationScreen>
    );
  }

  const invitation = invitationQuery.data;

  if (invitation.office.slug !== tenant.slug) {
    return (
      <InvitationScreen tenant={tenant}>
        <OtherOffice invitation={invitation} token={token} />
      </InvitationScreen>
    );
  }

  const submitRefusal = invitationRefusalOf(acceptMutation.error);

  return (
    <InvitationScreen tenant={tenant}>
      <Summary invitation={invitation} />

      {openSessionEmail && (
        <p className="mt-4 text-[13px] text-ink-muted">
          {`Você está conectado como ${openSessionEmail}; ao aceitar, essa sessão será encerrada`}
        </p>
      )}

      {invitation.existing_account ? (
        <p className="mt-6 text-[14px] text-ink">
          {`Sua conta ${invitation.email} será vinculada a ${invitation.client.razao_social}`}
        </p>
      ) : (
        <AccountForm form={form} errors={fieldErrors} onChange={updateField} />
      )}

      {submitRefusal && <SubmitRefusal refusal={submitRefusal} tenant={tenant} />}

      {/* A falha sem corpo `{code}` (rede, proxy) não tem recusa para ler: sem este aviso o
          aceite falharia calado, e o botão voltaria como se nada tivesse acontecido. */}
      {acceptMutation.isError && !submitRefusal && (
        <div className="mt-6">
          <Alert message={ACCEPT_INVITATION_FAILURE} />
        </div>
      )}

      <IonButton
        expand="block"
        className="rx-button mt-6"
        disabled={acceptMutation.isPending}
        onClick={() => accept(invitation)}
      >
        {acceptMutation.isPending ? <IonSpinner name="dots" /> : 'Aceitar convite'}
      </IonButton>
    </InvitationScreen>
  );
}

interface SubmitRefusalProps {
  refusal: InvitationRefusal;
  tenant: Tenant;
}

/**
 * A recusa do envio. `INVITATION_DATA_INVALID` não aparece aqui: cada mensagem dele já está no
 * campo a que pertence, e repeti-la só empurraria o botão para baixo.
 */
function SubmitRefusal({ refusal, tenant }: SubmitRefusalProps): JSX.Element | null {
  if (refusal.code === INVITATION_REFUSAL.dataInvalid) return null;

  if (refusal.code === INVITATION_REFUSAL.officeSuspended) {
    return (
      <div className="mt-6">
        <SuspendedNotice
          message={refusal.message}
          supportPhone={tenant.telefone_suporte}
          supportEmail={tenant.email_reply_to}
        />
      </div>
    );
  }

  return (
    <div className="mt-6">
      <Alert message={refusal.message} />
    </div>
  );
}

interface InvitationScreenProps {
  tenant: Tenant;
  children: ReactNode;
}

function InvitationScreen({ tenant, children }: InvitationScreenProps): JSX.Element {
  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="rx-container rx-screen px-6 pb-8 pt-12">
          <OfficeLogo tenant={tenant} />
          <h1 className="mt-8 text-[28px] font-medium tracking-tight text-ink">Convite</h1>
          {children}
        </div>
      </IonContent>
    </IonPage>
  );
}

function Loading(): JSX.Element {
  return (
    <output className="mt-8 flex items-center gap-2">
      <IonSpinner name="dots" aria-hidden="true" />
      <span className="sr-only">Carregando</span>
    </output>
  );
}

function Summary({ invitation }: { invitation: InvitationDetail }): JSX.Element {
  return (
    <>
      <p className="mt-6 text-[15px] text-ink">{invitationSentence(invitation)}</p>
      <p className="mt-4 font-mono text-[11px] uppercase tracking-overline text-ink-muted">
        Convite para
      </p>
      <p className="rx-mono text-[13px] text-ink">{invitation.email}</p>
      <p className="mt-4 font-mono text-[11px] uppercase tracking-overline text-ink-muted">
        Seu acesso
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {FISCAL_MODULES.map((module) => {
          const level = invitation.access[module];
          return (
            <span key={module} className={LEVEL_TAG_CLASS[level]}>
              {MODULE_LABEL[module]} · {ACCESS_LEVEL_LABEL[level]}
            </span>
          );
        })}
      </div>
      <p className="mt-4 text-[12px] text-ink-subtle">
        {`O convite vence em ${formatDateTime(invitation.expires_at)}`}
      </p>
    </>
  );
}

interface AccountFormProps {
  form: InvitationForm;
  errors: InvitationFieldErrors;
  onChange(field: InvitationField, value: string): void;
}

function AccountForm({ form, errors, onChange }: AccountFormProps): JSX.Element {
  return (
    <div className="mt-6 flex flex-col gap-5">
      <FieldWrap id="convite-nome" label="Nome completo" error={errors.full_name}>
        <MaskedInput
          id="convite-nome"
          value={form.full_name}
          onChange={(value) => onChange('full_name', value)}
          placeholder="Seu nome completo"
        />
      </FieldWrap>

      <FieldWrap id="convite-cpf" label="CPF" error={errors.cpf}>
        <MaskedInput
          id="convite-cpf"
          value={form.cpf}
          onChange={(value) => onChange('cpf', applyCpfMask(value))}
          placeholder="000.000.000-00"
          inputmode="numeric"
        />
      </FieldWrap>

      <FieldWrap id="convite-telefone" label="Telefone" error={errors.phone} optional>
        <MaskedInput
          id="convite-telefone"
          value={form.phone}
          onChange={(value) => onChange('phone', applyPhoneMask(value))}
          placeholder="(11) 90000-0000"
          inputmode="tel"
          type="tel"
        />
      </FieldWrap>

      <FieldWrap
        id="convite-senha"
        label="Senha"
        error={errors.password}
        hint="Mínimo de 8 caracteres"
      >
        <PasswordInput
          id="convite-senha"
          value={form.password}
          onChange={(value) => onChange('password', value)}
        />
      </FieldWrap>

      <FieldWrap
        id="convite-senha-confirmacao"
        label="Confirme a senha"
        error={errors.password_confirm}
      >
        <PasswordInput
          id="convite-senha-confirmacao"
          value={form.password_confirm}
          onChange={(value) => onChange('password_confirm', value)}
        />
      </FieldWrap>
    </div>
  );
}

interface PasswordInputProps {
  id: string;
  value: string;
  onChange(value: string): void;
}

function PasswordInput({ id, value, onChange }: PasswordInputProps): JSX.Element {
  return (
    <MaskedInput
      id={id}
      type="password"
      autocomplete="new-password"
      value={value}
      onChange={onChange}
    />
  );
}

interface RefusalProps {
  refusal: InvitationRefusal | null;
  tenant: Tenant;
  onRetry(): void;
}

function Refusal({ refusal, tenant, onRetry }: RefusalProps): JSX.Element {
  const layout = layoutOf(refusal);

  if (layout === REFUSAL_LAYOUT.suspended && refusal) {
    return (
      <div className="mt-6">
        <SuspendedNotice
          message={refusal.message}
          supportPhone={tenant.telefone_suporte}
          supportEmail={tenant.email_reply_to}
        />
      </div>
    );
  }

  if (layout === REFUSAL_LAYOUT.retry || !refusal) {
    return (
      <div className="mt-6">
        <Alert message={OPEN_INVITATION_FAILURE} />
        <IonButton expand="block" fill="outline" className="mt-4" onClick={onRetry}>
          Tentar novamente
        </IonButton>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <Alert message={refusal.message} />
      {layout === REFUSAL_LAYOUT.login && (
        <Link to={ROUTES.login} replace className="mt-4 inline-block text-[13px] text-brand">
          Ir para o login
        </Link>
      )}
    </div>
  );
}

interface OtherOfficeProps {
  invitation: InvitationDetail;
  token: string;
}

/**
 * O convite é de outro escritório (decisão 11 de E9): a conta nova precisa nascer na origem
 * daquele escritório, então esta tela só aponta o endereço certo.
 */
function OtherOffice({ invitation, token }: OtherOfficeProps): JSX.Element {
  const { nome, app_url: appUrl } = invitation.office;
  return (
    <div className="mt-6">
      <Alert message={`Este convite é de ${nome}`} />
      {appUrl ? (
        <a
          href={`${appUrl}${convitePath(token)}`}
          rel="noopener"
          className="mt-4 inline-block text-[13px] text-brand"
        >
          Abrir o convite
        </a>
      ) : (
        <p className="mt-4 text-[13px] text-ink-muted">
          Peça a quem convidou o endereço do aplicativo desse escritório.
        </p>
      )}
    </div>
  );
}
